import configService from '@/services/config'
import pushService from '@/services/push'

let configPromise = null

// set once this device follows the news topic: devices that followed a team / player before the news
// existed are added to it once (see ensureNewsTopic)
const NEWS_TOPIC_KEY = 'big-push-news'

// move the push subscriptions along with the team / player picked on the profile
const syncPushTopics = (state, previousTopics) => {
    if (!state.notifications) {
        return
    }
    const topics = pushService.topicsFor(state.team, state.player)
    pushService.updateTopics(
        topics.filter(topic => !previousTopics.includes(topic)),
        previousTopics.filter(topic => !topics.includes(topic))
    ).catch(error => console.error('Could not update push subscription', error))
}

export default {
    state: {
        team: localStorage.getItem('big-team') === 'null' ? null : JSON.parse(localStorage.getItem('big-team')),
        player: localStorage.getItem('big-player') === 'null' ? null : JSON.parse(localStorage.getItem('big-player')),
        config: localStorage.getItem('big-config') === 'null' ? null : JSON.parse(localStorage.getItem('big-config')),
        notifications: localStorage.getItem('big-notifications') === 'true',
        leagueFilter: null,
        seasonFilter: null,
        teamFilter: null
    },
    mutations: {
        setTeam(state, value) {
            state.team = value
        },
        setPlayer(state, value) {
            state.player = value
        },
        setLeagueFilter(state, value) {
            state.leagueFilter = value
        },
        setSeasonFilter(state, value) {
            state.seasonFilter = value
        },
        setTeamFilter(state, value) {
            state.teamFilter = value
        },
        setCourtFilter(state, value) {
            state.courtFilter = value
        },
        setConfig(state, value) {
            state.config = value
        },
        setNotifications(state, value) {
            state.notifications = value
        }
    },
    actions: {
        setConfig({ commit }, config) {
            localStorage.setItem('big-config', JSON.stringify(config))
            commit('setConfig', config)
        },
        ensureConfig({ commit }) {
            if (configPromise) {
                return configPromise
            }

            configPromise = configService.getConfig().then(resp => {
                localStorage.setItem('big-config', JSON.stringify(resp.data))
                commit('setConfig', resp.data)
                return resp.data
            }).finally(() => {
                configPromise = null
            })

            return configPromise
        },
        setTeam({ commit, state }, team) {
            const previousTopics = pushService.topicsFor(state.team, state.player)
            localStorage.setItem('big-team', JSON.stringify(team))
            commit('setTeam', team)
            syncPushTopics(state, previousTopics)
        },
        setPlayer({ commit, state }, player) {
            const previousTopics = pushService.topicsFor(state.team, state.player)
            localStorage.setItem('big-player', JSON.stringify(player))
            commit('setPlayer', player)
            syncPushTopics(state, previousTopics)
        },
        async setNotifications({ commit, state }, enabled) {
            const topics = pushService.topicsFor(state.team, state.player)
            if (enabled) {
                await pushService.updateTopics(topics, [])
            } else {
                await pushService.updateTopics([], topics)
            }
            localStorage.setItem('big-notifications', String(enabled))
            commit('setNotifications', enabled)
        },
        // Devices with notifications on from before the news topic: follow it too, once
        ensureNewsTopic({ state }) {
            const topics = pushService.topicsFor(state.team, state.player)
            if (!state.notifications || !topics.length || localStorage.getItem(NEWS_TOPIC_KEY)) {
                return
            }
            pushService.updateTopics(topics, [])
                .then(() => localStorage.setItem(NEWS_TOPIC_KEY, 'true'))
                .catch(error => console.error('Could not follow the news', error))
        },
        setLeagueFilter({ commit }, leagueFilter) {
            commit('setLeagueFilter', leagueFilter)
        },
        setSeasonFilter({ commit }, seasonFilter) {
            commit('setSeasonFilter', seasonFilter)
        },
        setTeamFilter({ commit }, teamFilter) {
            commit('setTeamFilter', teamFilter)
        }
    },
    getters: {
        getTeam: state => state.team,
        getNotifications: state => state.notifications,
        // the player picked on the profile is the captain (leader) of the team: they can ask for postponements
        getCaptain: state => !!state.team && !!state.player && !!state.player.leader,
        getPlayer: state => state.player,
        getLeagueFilter: state => state.leagueFilter,
        getSeasonFilter: state => state.seasonFilter,
        getTeamFilter: state => state.teamFilter,
        getConfig: state => JSON.parse(JSON.stringify(state.config))
    }
}
